export interface Job {
  id: string;
  title: string;
  department: string;
  location: string;
  type: 'Full-time' | 'Part-time' | 'Contract' | 'Internship';
  experience: string;
  salary?: string;
  description: string;
  requirements: string[];
  responsibilities: string[];
  benefits: string[];
  skills: string[];
  postedDate: string;
  applicationDeadline?: string;
  featured?: boolean;
}

// Extended interface for detailed job data from JOB_OPENINGS
export interface JobData extends Job {
  workMode: string;
  jobSummary: string;
  preferredQualifications: string[];
  teamInfo: string;
  relocationSupport?: boolean;
  applicationLink?: string;
  expirationDate?: string;
}

// Time remaining interface for countdown
export interface TimeRemaining {
  hours: number;
  minutes: number;
  isExpired: boolean;
}

// Utility function to calculate time until expiration
export function getTimeUntilExpiration(expirationDate: string): TimeRemaining {
  const now = new Date().getTime();
  const expiration = new Date(expirationDate).getTime();
  const difference = expiration - now;

  if (difference <= 0) {
    return {
      hours: 0,
      minutes: 0,
      isExpired: true
    };
  }

  const hours = Math.floor(difference / (1000 * 60 * 60));
  const minutes = Math.floor((difference % (1000 * 60 * 60)) / (1000 * 60));

  return {
    hours,
    minutes,
    isExpired: false
  };
}

// Utility function to filter active (non-expired) jobs
export function filterActiveJobs<T extends { expirationDate?: string }>(jobs: T[]): T[] {
  const now = new Date().getTime();
  return jobs.filter(job => {
    if (!job.expirationDate) return true;
    const expiration = new Date(job.expirationDate).getTime();
    return expiration > now;
  });
}

// Utility function to format posted date
export function formatPostedDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = Math.abs(now.getTime() - date.getTime());
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays} days ago`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `${weeks} ${weeks === 1 ? 'week' : 'weeks'} ago`;
  }
  if (diffDays < 365) {
    const months = Math.floor(diffDays / 30);
    return `${months} ${months === 1 ? 'month' : 'months'} ago`;
  }
  
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
}

// Utility function to format expiration date
export function formatExpirationDate(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffTime = date.getTime() - now.getTime();
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

  if (diffDays < 0) return 'Expired';
  if (diffDays === 0) return 'Expires today';
  if (diffDays === 1) return 'Expires tomorrow';
  if (diffDays < 7) return `Expires in ${diffDays} days`;
  if (diffDays < 30) {
    const weeks = Math.floor(diffDays / 7);
    return `Expires in ${weeks} ${weeks === 1 ? 'week' : 'weeks'}`;
  }
  
  return date.toLocaleDateString('en-US', { 
    year: 'numeric', 
    month: 'short', 
    day: 'numeric' 
  });
}

// Utility function to check if a job is currently active (not expired)
export function isJobActive(job: { expirationDate?: string }): boolean {
  if (!job.expirationDate) return true;
  const now = new Date().getTime();
  const expiration = new Date(job.expirationDate).getTime();
  return expiration > now;
}